<template>
  <div class="layout-builder">
    <DraggableList
      class="editing"
      @update:list="emits('update:layout', $event)"
      :list="layout"
    >
      <template #item="{ object }">
        <div class="edit-panel">
          <Button
            @click.prevent="changeSelected(object.id)"
            icon="pi pi-arrows-v"
            class="handle"
            :label="'' + object.id"
          />
        </div>
        <LayoutItem
          class="flex-stretch"
          :layout="object"
          @update="handleUpdate(object, $event)"
        />
      </template>
    </DraggableList>
    <Dialog
      @update:visible="handleVisibleChanged"
      :visible="!!selected"
      modal
      style="max-width: 95vw; max-height: 40vh"
      ref="editor"
    >
      <template #header>
        <div v-if="!!selected">
          <Button
            @click.prevent="deleteItem(selected)"
            icon="pi pi-trash"
            label="Delete"
            severity="danger"
          />
          <Button :label="`${selected.type}-${selected.id}`" text />
        </div>
      </template>
      <template #default>
        <div v-if="selected">
          <Component
            v-if="layouts.manifest[selected.type]?.form"
            :is="layouts.formBuilder"
            :fields="layouts.manifest[selected.type].form"
            :form-component="layouts.form"
            :modelValue="selected.options"
            :defaults="layouts.manifest[selected.type].defaults"
            :sub="true"
            @update:modelValue="handleUpdate(selected, $event)"
          />
        </div>
      </template>
      <template #footer>
        <div v-if="!!selected" class="sample">
          <LayoutItem
            :layout="selected"
            :editing="false"
            @update="handleUpdate(selected, $event)"
          />
        </div>
      </template>
    </Dialog>
    <form @submit.prevent="addItem" class="card">
      <Toolbar>
        <template #end>
          <Dropdown
            v-model="cardType"
            :options="options"
            placeholder="Component..."
            option-label="label"
            option-value="value"
          />
          <Dropdown v-model="placement" :options="['top', 'bottom']" />
          <Button type="submit">Add</Button>
        </template>
      </Toolbar>
    </form>
  </div>
</template>

<script setup lang="ts">
import copy from "@virgodev/bazaar/functions/copy";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import OverlayPanel from "primevue/overlaypanel";

import Toolbar from "primevue/toolbar";
import { computed, ref } from "vue";
import { useLayoutStore } from "../stores/layouts";
import type { LayoutCard } from "../utils/layout";
import DraggableList from "./DraggableList.vue";
import LayoutItem from "./LayoutItem.vue";

const props = defineProps<{
  layout: LayoutCard[];
}>();

const layouts = useLayoutStore();

const selectedId = ref<number>(0);
const emits = defineEmits(["update:layout"]);
const cardType = ref<string>("");
const placement = ref<string>("bottom");
const items = ref<{ [key: number]: Element }>({});
const editor = ref<OverlayPanel | null>(null);

const selected = computed(() => {
  return props.layout.find((i) => i.id === selectedId.value);
});

const options = computed(() => {
  const retval = [];
  for (const key in layouts.manifest) {
    retval.push({ label: layouts.manifest[key].label || key, value: key });
  }
  return retval;
});

function handleVisibleChanged(event: boolean) {
  if (!event) {
    selectedId.value = -1;
  }
}
function changeSelected(id: number) {
  if (selectedId.value === id) {
    selectedId.value = -1;
  } else {
    selectedId.value = id;
  }
}
function handleUpdate(original: LayoutCard, $event: any) {
  const layout = copy(props.layout);
  const card = layout.find((l: LayoutCard) => l.id === original.id);
  for (const prop in $event) {
    // add ids to inlines
    if ($event[prop] instanceof Array) {
      const array: any[] = $event[prop];
      for (const row of array) {
        if (!row.id) {
          row.id =
            array.reduce((a: number, b: any) => Math.max(a, b.id || 0), 0) + 1;
        }
      }
    }
    card.options[prop] = $event[prop];
  }
  emits("update:layout", layout);
}
function addItem() {
  if (cardType.value) {
    const layout = copy(props.layout);
    const item = {
      id: layout.reduce((a, b) => Math.max(a, b.id || 0), 1) + 1,
      type: cardType.value,
      options: {},
    };
    if (placement.value === "top") {
      layout.unshift(item);
    } else {
      layout.push(item);
    }
    emits("update:layout", layout);
    cardType.value = "";
  }
}
function deleteItem(original: LayoutCard) {
  if (confirm(`Delete this item? (${original.id})`)) {
    const layout = copy(props.layout);
    const cardIndex = layout.findIndex((l: LayoutCard) => l.id === original.id);
    layout.splice(cardIndex, 1);
    emits("update:layout", layout);
  }
}
</script>

<style scoped>
.layout-builder :deep(.item) {
  display: flex;
  gap: 5px;
  flex-direction: row;
  position: relative;
  min-height: 40px;
  padding: 0.5em;
}
.editing {
}
.edit-panel {
  top: 1px;
  left: 1px;
  z-index: 100;
}
.d-flex {
  display: flex;
}
.flex-row {
  flex-direction: row;
}
.layout-builder .debug-border {
  border: 2px solid green;
}
.sample {
  display: none;
  margin-top: 5px;
  padding: 5px;
  width: 100%;
  max-height: 50vh;
  min-height: 1px;
  overflow: scroll;
}
</style>
